import styled, { css, keyframes } from 'styled-components';

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/** Small inline "updating" spinner — inherits color from its container. */
export const Spinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  vertical-align: -1px;
  flex-shrink: 0;
`;

/** Neutral explanatory block (glossary / how-it-is-computed notes). */
export const InfoNote = styled.div`
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 8px 0 12px;
  font-size: 12px;
  line-height: 1.5;
`;

type ChipVariant = 'warn' | 'ok' | 'error' | 'neutral';

const CHIP_COLORS: Record<
  ChipVariant,
  { bg: string; border: string; fg: string }
> = {
  warn: { bg: '#fffbeb', border: '#fde68a', fg: '#92400e' },
  ok: { bg: '#f0fdf4', border: '#bbf7d0', fg: '#166534' },
  error: { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b' },
  neutral: { bg: '#f8fafc', border: '#e2e8f0', fg: '#475569' },
};

/** Sticky pill that stays visible while scrolling — live sync status. */
export const StickyChip = styled.div<{ variant: ChipVariant }>`
  position: sticky;
  top: 12px;
  z-index: 30;
  margin-left: auto;
  width: max-content;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ variant }) => CHIP_COLORS[variant].bg};
  border: 1px solid ${({ variant }) => CHIP_COLORS[variant].border};
  color: ${({ variant }) => CHIP_COLORS[variant].fg};
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
`;

export const UpdatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-weight: 600;
`;

/* Visual tokens ported from docs/pandadoc-dashboard/public/index.html. */

export const Wrapper = styled.div`
  width: 100%;
  margin: 32px 0 64px;
  color: #0f172a;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
`;

export const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0;
  font-weight: 650;
  letter-spacing: -0.01em;
`;

/** Manual «sync everything» trigger — always visible, even when up to date. */
export const SyncAllButton = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #2563eb;
  background: #2563eb;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #1d4ed8;
    border-color: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const ReloadButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;

  &:hover {
    border-color: #cbd5e1;
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const SubTitle = styled.div`
  color: #64748b;
  font-size: 13px;
  margin-bottom: 16px;
`;

export const SectionTitle = styled.h2`
  font-size: 13px;
  margin: 24px 0 10px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const KpiGrid = styled.div<{ columns: number }>`
  display: grid;
  gap: 10px;
  margin-bottom: 10px;
  grid-template-columns: repeat(${({ columns }) => columns}, 1fr);
`;

export const KpiCardBox = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
`;

export const KpiLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

export const KpiValue = styled.div<{ accent?: string }>`
  font-size: 22px;
  font-weight: 700;
  margin-top: 4px;
  color: ${({ accent }) => accent || '#0f172a'};
`;

export const KpiSubLabel = styled.div`
  color: #94a3b8;
  font-size: 11px;
  margin-top: 2px;
`;

export const Banner = styled.div<{ variant: 'warn' | 'error' }>`
  border-radius: 10px;
  padding: 10px 14px;
  margin: 12px 0;
  font-size: 12px;
  line-height: 1.5;
  border: 1px solid
    ${({ variant }) => (variant === 'error' ? '#fecaca' : '#fde68a')};
  background: ${({ variant }) => (variant === 'error' ? '#fef2f2' : '#fffbeb')};
  color: ${({ variant }) => (variant === 'error' ? '#991b1b' : '#92400e')};
`;

export const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border-bottom: 1px solid #e2e8f0;
  margin: 16px 0 4px;
`;

export const TabBarItem = styled.button<{ active: boolean }>`
  border: 0;
  background: transparent;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ active }) => (active ? '#0f172a' : '#64748b')};
  border-bottom: 2px solid
    ${({ active }) => (active ? '#0f172a' : 'transparent')};
  margin-bottom: -1px;
  transition: color 0.15s;

  &:hover {
    color: #0f172a;
  }
`;

export const Controls = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  margin: 16px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
`;

export const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ControlLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
`;

export const Tabs = styled.div`
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
`;

export const Tab = styled.button<{ active: boolean }>`
  border: 0;
  background: ${({ active }) => (active ? '#fff' : 'transparent')};
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ active }) => (active ? '#0f172a' : '#64748b')};
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s;
  box-shadow: ${({ active }) =>
    active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'};
`;

export const PillGroup = styled.div`
  display: inline-flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const Pill = styled.button<{ active: boolean }>`
  border: 1px solid ${({ active }) => (active ? '#0f172a' : '#e2e8f0')};
  background: ${({ active }) => (active ? '#0f172a' : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#475569')};
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${({ active }) => (active ? '#0f172a' : '#cbd5e1')};
  }
`;

export const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 12px;
`;

export const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Dot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
  background: ${({ color }) => color};
`;

export const CardMeta = styled.div`
  font-size: 12px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
`;

export const Legend = styled.div`
  display: inline-flex;
  gap: 12px;
  font-size: 11px;
  color: #64748b;

  & > span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
`;

export const ChartWrap = styled.div<{ tall?: boolean }>`
  position: relative;
  height: ${({ tall }) => (tall ? '240px' : '200px')};
`;

export const FooterNote = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 8px;
`;

export const Grid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const Grid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const DimWhenFetching = styled.div<{ fetching: boolean }>`
  transition: opacity 0.15s;
  ${({ fetching }) =>
    fetching &&
    css`
      opacity: 0.45;
      pointer-events: none;
    `}
`;

export const ListHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

export const SearchInput = styled.input`
  width: 100%;
  max-width: 300px;
  margin-left: auto;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;

  &:focus {
    border-color: #6366f1;
    background: #fff;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    font-weight: 600;
    padding: 10px 8px;
    border-bottom: 1px solid #e2e8f0;
  }

  tbody td {
    padding: 10px 8px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

export const DateCell = styled.td`
  color: #64748b;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

export const NumCell = styled.td`
  color: #0f172a;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
`;

export const NameCell = styled.td`
  font-weight: 500;
  color: #0f172a;
`;

const badgeColors: Record<string, { bg: string; fg: string }> = {
  student: { bg: '#ede9fe', fg: '#5b21b6' },
  trainer: { bg: '#cffafe', fg: '#155e75' },
  dofin: { bg: '#ffedd5', fg: '#9a3412' },
  other: { bg: '#f1f5f9', fg: '#475569' },
  signed: { bg: '#dcfce7', fg: '#166534' },
  active: { bg: '#fef3c7', fg: '#92400e' },
  lost: { bg: '#fee2e2', fg: '#991b1b' },
};

export const Badge = styled.span<{ variant: string }>`
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  white-space: nowrap;
  background: ${({ variant }) => badgeColors[variant]?.bg || '#f1f5f9'};
  color: ${({ variant }) => badgeColors[variant]?.fg || '#475569'};
`;

export const CourseBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  white-space: nowrap;
  background: #f8fafc;
  color: #334155;
  border: 1px solid #e2e8f0;
`;

export const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;

export const LoadMoreButton = styled.button`
  display: block;
  margin: 16px auto 0;
  padding: 8px 18px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;

  &:hover {
    border-color: #cbd5e1;
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const CenterLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;

  & > div {
    width: 48px;
    height: 48px;
  }
`;
